import { useEffect } from "react";

export const SITE_NAME = "TotalReach";
export const DEFAULT_DESCRIPTION = "TotalReach is the Political Campaign Operating System — CTV-first advertising, FEC-compliant fundraising, race intelligence and AI creative for 2026 campaigns.";
export const siteOrigin = () => (typeof window !== "undefined" && window.location?.origin) || "https://totalreach.us";

export const orgJsonLd = () => ({
  "@context": "https://schema.org", "@type": "Organization", name: SITE_NAME, url: siteOrigin(),
  logo: `${siteOrigin()}/favicon-192.png`, email: "info@totalreach.us", telephone: "+1-469-991-1289",
  address: { "@type": "PostalAddress", addressRegion: "TX", addressCountry: "US" },
  sameAs: ["https://totalreach.us"],
});

export const softwareJsonLd = (offers = []) => ({
  "@context": "https://schema.org", "@type": "SoftwareApplication", name: SITE_NAME, applicationCategory: "BusinessApplication",
  operatingSystem: "Web", url: siteOrigin(), description: DEFAULT_DESCRIPTION,
  offers: offers.map((o) => ({ "@type": "Offer", name: o.name, price: String(o.price), priceCurrency: "USD", url: `${siteOrigin()}/pricing`,
    ...(o.billing ? { priceSpecification: { "@type": "UnitPriceSpecification", price: String(o.price), priceCurrency: "USD", unitText: o.billing } } : {}) })),
});

export const faqJsonLd = (faq) => ({
  "@context": "https://schema.org", "@type": "FAQPage",
  mainEntity: faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
});

export const raceEventJsonLd = (race, path) => ({
  "@context": "https://schema.org", "@type": "Event", name: `2026 General Election — ${race.label}`,
  startDate: "2026-11-03", eventStatus: "https://schema.org/EventScheduled", eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  location: { "@type": "Place", name: race.state_name || race.state, address: { "@type": "PostalAddress", addressRegion: race.state, addressCountry: "US" } },
  organizer: { "@type": "Organization", name: "Federal Election Commission", url: "https://www.fec.gov" },
  description: `Candidates, FEC fundraising and campaign intelligence for the ${race.label} race on TotalReach.`,
  url: `${siteOrigin()}${path}`,
});

export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org", "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: `${siteOrigin()}${it.path}` })),
});

const upsert = (selector, tag, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) { el = document.createElement(tag); document.head.appendChild(el); }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};
const meta = (key, name, content) => upsert(`meta[${key}="${name}"]`, "meta", { [key]: name, content });

/* Head manager: the deepest mounted <Seo> wins (React runs child effects first, so page-level tags are set after
   route-wrapper defaults). Idempotent upserts keep exactly one of each tag; JSON-LD blocks are replaced per page. */
export const Seo = ({ title, description = DEFAULT_DESCRIPTION, path, image = "/favicon-192.png", type = "website", noindex = false, jsonLd }) => {
  const fullTitle = title ? (title.includes(SITE_NAME) ? title : `${title} · ${SITE_NAME}`) : `${SITE_NAME} — The Political Campaign Operating System`;
  const url = `${siteOrigin()}${path ?? (typeof window !== "undefined" ? window.location.pathname : "/")}`;
  const img = image.startsWith("http") ? image : `${siteOrigin()}${image}`;
  const ld = JSON.stringify(jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []);

  useEffect(() => {
    document.title = fullTitle;
    meta("name", "description", description);
    meta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    upsert('link[rel="canonical"]', "link", { rel: "canonical", href: url });
    meta("property", "og:site_name", SITE_NAME);
    meta("property", "og:type", type);
    meta("property", "og:title", fullTitle);
    meta("property", "og:description", description);
    meta("property", "og:url", url);
    meta("property", "og:image", img);
    meta("name", "twitter:card", "summary_large_image");
    meta("name", "twitter:title", fullTitle);
    meta("name", "twitter:description", description);
    meta("name", "twitter:image", img);
    document.head.querySelectorAll("script[data-seo-jsonld]").forEach((s) => s.remove());
    JSON.parse(ld).forEach((block) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "1");
      s.textContent = JSON.stringify(block);
      document.head.appendChild(s);
    });
    return () => { document.head.querySelectorAll("script[data-seo-jsonld]").forEach((s) => s.remove()); };
  }, [fullTitle, description, url, img, type, noindex, ld]);

  return null;
};
